<template>
  <b-dropdown
    split
    ref="multiSelectDropdown"
    :class="`multi-select-dropdown ${getCustomClass}`"
    :variant="isDarkMode ? 'dark' : 'light'"
    :disabled="disabled"
  >
    <template #button-content>
      <div
        v-if="(!isSelectedAll || !optimized) && currentSelectedItems.length > 0"
        class="multi-select-dropdown--selected"
        style="padding: .375rem .75rem;"
        @click="showDropdown"
      >
        <b-badge
          v-for="selectedItem in currentSelectedItems"
          :key="selectedItem.value"
          class="multi-select-dropdown--selected-item mr-1"
          variant="info"
        >
          {{ selectedItem.label }}&nbsp;&nbsp;&nbsp;<span class="deselect-item" @click="($event) => selectOption($event, selectedItem)">&times;</span>
        </b-badge>
      </div>
      <span
        v-else-if="isSelectedAll && optimized"
        class="multi-select-dropdown--selected-none d-inline-flex w-100 h-100 align-items-center"
        style="padding: .375rem .75rem;"
        @click="showDropdown"
      >
        {{ selectedAllLabel }}
      </span>
      <span
        v-else
        class="multi-select-dropdown--selected-none d-inline-flex w-100 h-100 align-items-center"
        style="padding: .375rem .75rem;"
        @click="showDropdown"
      >
        {{ placeholder }}
      </span>
    </template>
    <b-dropdown-header v-if="showSearch" class="multi-select-dropdown--search">
      <b-form-input v-model="search" placeholder="Search items..."></b-form-input>
    </b-dropdown-header>
    <b-dropdown-item
      v-if="showSelectAll"
      class="multi-select-dropdown--item"
      @click="selectAll"
    >
      Select All
    </b-dropdown-item>
    <b-dropdown-item
      v-if="showDeselectAll"
      class="multi-select-dropdown--item"
      @click="deselectAll"
    >
      Deselect All
    </b-dropdown-item>
    <b-dropdown-item
      v-for="dropdownItem in filteredDropdownItems"
      class="multi-select-dropdown--item"
      :key="dropdownItem.value"
      @click="selectOption($event, dropdownItem)"
    >
      <div class="multi-select-dropdown--item-check">
        <b-form-checkbox :checked="dropdownItem.checked"></b-form-checkbox>
      </div>
      <div class="multi-select-dropdown--item-label">{{ dropdownItem.label }}</div>
    </b-dropdown-item>
  </b-dropdown>
</template>

<script>
/* eslint-disable */
import { mapGetters } from 'vuex';
export default {
  name: "MultiSelectDropdown",
  props: {
    customClass: {
      type: [String, Array],
      default: () => [],
    },
    value: {
      type: [String, Array],
      default: () => [],
    },
    options: {
      type: Array,
      default: () => [],
    },
    disabled: {
      type: Boolean,
      default: false,
    },
    placeholder: {
      type: String,
      default: "",
    },
    selectedAllLabel: {
      type: String,
      default: "Selected All"
    },
    showSearch: {
      type: Boolean,
      default: false,
    },
    showSelectAll: {
      type: Boolean,
      default: false,
    },
    showDeselectAll: {
      type: Boolean,
      default: false,
    },
    optimized: {
      type: Boolean,
      default: false,
    }
  },
  data() {
    return {
      dropdownItems: [],
      filteredDropdownItems: [],
      currentValue: [],
      search: "",
    };
  },
  computed: {
    ...mapGetters("user", ["isDarkMode"]),
    getCustomClass() {
      if (Array.isArray(this.customClass) && this.customClass.length > 0) {
        return this.customClass.join(" ");
      } else if (typeof this.customClass === "string") {
        return this.customClass;
      } else {
        return "";
      }
    },
    currentSelectedItems() {
      return this.dropdownItems.filter(item => item.checked);
    },
    isSelectedAll() {
      const selectedItems = this.dropdownItems.filter(item => item.checked);
      const isSelectAll = selectedItems.length === this.dropdownItems.length && this.dropdownItems.length > 0;
      return isSelectAll;
    }
  },
  methods: {
    initDropdown(initValue) {
      if (Array.isArray(initValue) && initValue.length > 0) {
        this.currentValue = [...initValue];
      } else if (typeof initValue === 'string' && initValue) {
        this.currentValue = [initValue];
      } else {
        this.currentValue = [];
      }
      this.dropdownItems = this.options.map(option => {
        const { value } = option;
        return {
          ...option,
          checked: this.currentValue.includes(value),
        };
      });
      this.getFilteredItems(this.search);
    },
    emitChanges() {
      const selectedItems = this.dropdownItems.filter(item => item.checked);
      this.$emit('input', selectedItems.map(item => item.value));
      this.$emit('selected', selectedItems);
    },
    selectOption($event, option) {
      $event.stopPropagation();
      const { value, checked, label } = option;
      const dropdownItemIndex = this.dropdownItems.findIndex(item => item.value === value);
      if (dropdownItemIndex >= 0) {
        this.dropdownItems.splice(dropdownItemIndex, 1, { label, value, checked: !checked });
        this.getFilteredItems(this.search);
        this.emitChanges();
      }
    },
    getFilteredItems(searchQuery) {
      if (!searchQuery || searchQuery === "") {
        this.filteredDropdownItems = [...this.dropdownItems];
      } else {
        searchQuery = searchQuery.toLowerCase();
        this.filteredDropdownItems = this.dropdownItems.filter(item => {
          const { label } = item;
          const lowerCasedLabel = label.toLowerCase();
          return lowerCasedLabel.includes(searchQuery);
        });
      }
    },
    selectAll() {
      const filteredItems = this.filteredDropdownItems.map(item => item.label);
      if (filteredItems.length > 0) {
        this.dropdownItems.forEach(item => {
          const { label } = item;
          if (filteredItems.includes(label)) {
            item.checked = true;
          }
        })
      } else {
        this.dropdownItems.forEach(item => {
          item.checked = true;
        });
      }
      this.getFilteredItems(this.search);
      this.emitChanges();
    },
    deselectAll() {
      const filteredItems = this.filteredDropdownItems.map(item => item.label);
      if (filteredItems.length > 0) {
        this.dropdownItems.forEach(item => {
          const { label } = item;
          if (filteredItems.includes(label)) {
            item.checked = false;
          }
        })
      } else {
        this.dropdownItems.forEach(item => {
          item.checked = false;
        });
      }
      this.getFilteredItems(this.search);
      this.emitChanges();
    },
    showDropdown() {
      this.$refs.multiSelectDropdown.show();
    }
  },
  watch: {
    search(value) {
      this.getFilteredItems(value);
    },
    value: {
      handler(value) {
        this.initDropdown(value);
      },
      deep: true,
      immediate: true,
    },
  },
};
</script>

<style lang="scss">
.multi-select-dropdown {
  width: 100%;
  min-width: 120px;
  &.btn-group {
    >button {
      text-align: left;
      &:first-child {
        padding: 0;
      }
    }
    .dropdown-toggle-split {
      flex: unset;
    }
  }
  &--selected {
    &-item {
      .deselect-item {
        cursor: pointer;
      }
    }
  }
  &--selected-none {

  }
  &--item {
    .dropdown-item {
      display: flex;
      align-items: center;
    }
  }
  ul.dropdown-menu {
    max-height: 300px;
    overflow-y: auto;
  }
}
</style>